import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { blockDeleteWithMembers, handleHasMembersError } from '../components/RemoveMember';
import { useAuth } from '../auth/AuthContext';
import api from '../lib/api';
import useConfirmPurchase from '../lib/useConfirmPurchase';
import { Avatar, PageHead, Section, Spinner, ErrorNote, Empty, useAsync, useRefreshOnFocus } from '../components/ui';
import NotificationComposer from '../components/NotificationComposer';
import AssociationTabs from '../components/AssociationTabs';
import RelatedStrips from '../components/RelatedStrips';
import PriceField from '../components/PriceField';
import StatusPill from '../components/StatusPill';
import { AnnounceToggle } from '../components/AnnounceField';
import PreviewModal from '../components/PreviewModal';
import Conversation, { ConversationSetup, EMPTY_CONVERSATION_SETUP, applyConversationSetup } from '../components/Conversation';
import ColorField, { cssBackground } from '../components/ColorField';
import IconColorField from '../components/IconColorField';
import ProgramHero from '../components/ProgramHero';
import BackCrumb, { useBackTarget, fromHere } from '../components/BackCrumb';

/** Build the editable form-state snapshot from a loaded group. */
function toForm(group) {
  return {
    name: group.name || '',
    description: group.description || '',
    location: group.location || '',
    priceCents: group.priceCents || 0,
    coverColor: group.coverColor || '',
    iconColor: group.iconColor || '',
    isPrivate: !!group.isPrivate,
  };
}

/**
 * Group detail page. Two modes:
 *
 *   `/group/:id`         — view-only (About, join CTA).
 *   `/leader/group/:id`  — inline editor for the leader (name, description,
 *                          location, ticket price + icon upload) plus
 *                          notifications composer + delete. Save button in
 *                          the header commits changes. See design/021.
 */
export default function GroupDetailPage({ manageMode = false, preview = false } = {}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { me } = useAuth();
  const fromHome = useLocation().state?.from === 'home';
  // `?created=1` is set by the "+ New group" flow when it drafts an empty
  // group and drops the publisher here. We use it to hide setup-time-only
  // affordances (NotificationComposer + Danger zone) until the publisher
  // has committed at least one save.
  const [createParams, setCreateParams] = useSearchParams();
  const back = useBackTarget({
    home: 'My Groups',
    fallback: manageMode ? { path: '/leader/group', label: 'Manage groups' } : { path: '/groups', label: 'Groups' },
  });

  const { loading, error, data, reload, refresh } = useAsync(async () => {
    const group = await api.getGroup(id);
    return { group };
  }, [id]);
  const purchase = useConfirmPurchase(reload);
  // Members' view only — the owner's editor keeps its unsaved changes.
  useRefreshOnFocus(refresh, !manageMode && !preview);

  // Never saved yet ("+ New group" → ?created=1): Cancel discards it.
  const justCreated = createParams.get('created') === '1';
  // Draft = saved but not published (hidden from members) — 2026-10-12.
  const isDraft = !!data?.group?.draft;

  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [iconUrl, setIconUrl] = useState(null);
  const [iconBusy, setIconBusy] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  // design/026 — conversation set-up collected while the group is a draft;
  // saved together with everything else when the leader presses Create.
  const [convSetup, setConvSetup] = useState(EMPTY_CONVERSATION_SETUP);
  const [previewing, setPreviewing] = useState(false);
  const iconRef = useRef(null);

  useEffect(() => {
    if (data?.group) {
      setF(toForm(data.group));
      setIconUrl(data.group.eventIconUrl);
    }
  }, [data?.group?.id, data?.group?.updatedAt]);

  // Full-page spinner only on the first load; reloads keep the page (and
  // any unsent Conversation draft) mounted.
  if ((loading && !data) || (data?.group && !f)) return <div className="page"><Spinner /></div>;
  if (error) {
    const friendly = error.status === 403 ? "You don't have access to this group."
      : error.status === 404 ? 'This group no longer exists.' : null;
    return (
      <div className="page">
        <PageHead breadcrumb={<BackCrumb to={back} />} title={friendly ? 'Group unavailable' : 'Couldn’t load group'} />
        {friendly ? <Section><Empty>{friendly}</Empty></Section> : <ErrorNote error={error} onRetry={reload} />}
      </div>
    );
  }

  const { group } = data;
  const isGroupAdmin = !!(group.admin && me?.id && group.admin.id === me.id);
  // Preview (from the editor) renders the non-member view even for the leader.
  const canAdminister = !preview && manageMode && (isGroupAdmin || !!me?.isAdmin);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const dirty = canAdminister && f && JSON.stringify(f) !== JSON.stringify(toForm(group));

  const onIcon = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setIconBusy(true);
    try { const updated = await api.uploadGroupIcon(id, file); setIconUrl(updated?.eventIconUrl || iconUrl); reload(); }
    catch (err) { alert(err.message); }
    finally { setIconBusy(false); }
  };

  const removeIcon = async () => {
    if (!window.confirm('Remove the icon image (revert to the color)?')) return;
    setIconBusy(true);
    try { await api.deleteGroupIcon(id); setIconUrl(null); reload(); }
    catch (err) { alert(err.message); }
    finally { setIconBusy(false); }
  };

  // keepDraft: Preview while creating saves the form but keeps the draft
  // hidden; the Create / Save button publishes.
  // draft: true = hide (Unpublish), false = Publish, undefined = keep as is.
  // fromPreview: Preview's save — keeps the "new" marker so Cancel still discards.
  const save = async ({ draft, fromPreview = false } = {}) => {
    if (!f.name.trim()) return false;
    setBusy(true);
    try {
      await api.updateGroup(id, {
        name: f.name.trim(),
        // "" clears on the gateway (null = leave as is), so emptied fields save.
        description: f.description.trim(),
        location: f.location.trim(),
        priceCents: Number(f.priceCents) || 0,
        coverColor: f.coverColor || '',
        iconColor: f.iconColor || '',
        isPrivate: !!f.isPrivate,
        draft,
      });
      // First real save clears the create marker so the composer + danger
      // zone come back on subsequent visits.
      if (justCreated && !fromPreview) {
        // Create: also save the conversation set-up (settings + opening post).
        try {
          await applyConversationSetup('group', id, convSetup);
          setConvSetup(EMPTY_CONVERSATION_SETUP);
        } catch (e) {
          alert(`Group created, but the conversation set-up couldn't be saved: ${e?.data?.message || e.message}`);
        }
        const p = new URLSearchParams(createParams);
        p.delete('created');
        setCreateParams(p, { replace: true });
      }
      reload();
      return true;
    } catch (e) {
      if (!handleHasMembersError(e, 'groups', id, navigate)) alert(e?.data?.message || e.message);
      return false;
    }
    finally { setBusy(false); }
  };
  const publish = () => save({ draft: false });
  const unpublish = () => {
    if (!window.confirm('Unpublish? It will be hidden from members until you publish it again.')) return;
    save({ draft: true });
  };

  // Preview: save first, then open the non-member view in a modal.
  const openPreview = async () => {
    if (dirty && !(await save({ fromPreview: true }))) return;
    setPreviewing(true);
  };

  // Cancel: a never-saved draft (?created=1) is discarded entirely so
  // "Untitled group" stubs don't pile up; otherwise unsaved edits are dropped.
  // Either way we return to wherever the user came from.
  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate(justCreated ? '/leader/group' : `/group/${id}`);
  };
  const cancel = async () => {
    if (justCreated) {
      if (dirty && !window.confirm('Discard this new group?')) return;
      setCancelling(true);
      try { await api.deleteGroup(id); } catch (e) { alert(e.message); setCancelling(false); return; }
      // The draft no longer exists — go to the list (don't rely on history,
      // which could lead back to this deleted page).
      navigate(back.path, { replace: true });
      return;
    }
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    goBack();
  };

  const del = async () => {
    // Warn with the member count (leader excluded) so the leader knows who
    // loses access. Paid groups get a refund reminder — people paid to join.
    const others = (group.members || []).filter((m) => !(group.admin && m.id === group.admin.id)).length;
    // Decided 2026-10-02 — remove members first (refunds happen there).
    if (blockDeleteWithMembers(others, 'group', 'groups', id, navigate)) return;
    if (!window.confirm(`Permanently delete "${group.name}"? This cannot be undone.`)) return;
    try { await api.deleteGroup(id); navigate(manageMode ? '/leader/group' : '/groups'); }
    catch (e) { if (!handleHasMembersError(e, 'groups', id, navigate)) alert(e?.data?.message || e.message); }
  };

  return (
    <div className="page">
      <PageHead
        breadcrumb={<BackCrumb to={back} />}
        title=""
        actions={
          canAdminister
            ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <StatusPill draft={isDraft} />
                <button className="btn" disabled={busy || cancelling} onClick={cancel}>
                  {cancelling ? 'Cancelling…' : 'Cancel'}
                </button>
                <button className="btn" disabled={busy || cancelling || !f.name.trim()} onClick={() => save()}>
                  {busy ? 'Saving…' : 'Save'}
                </button>
                <button className="btn" disabled={busy || cancelling || !f.name.trim()} onClick={openPreview}>Preview</button>
                {isDraft
                  ? <button className="btn btn-primary" disabled={busy || cancelling || !f.name.trim()} onClick={publish}>Publish</button>
                  : <button className="btn" disabled={busy || cancelling} onClick={unpublish}>Unpublish</button>}
              </div>
            )
            : ((isGroupAdmin || me?.isAdmin) && !preview
                ? <button className="btn btn-primary" onClick={() => navigate(`/leader/group/${id}`, fromHere(`/group/${id}`, group.name))}>Manage group →</button>
                : null)
        }
      />

      <ProgramHero kind="Group" name={canAdminister ? (f.name || group.name) : group.name}
        iconSrc={api.iconUrl(iconUrl || group.eventIconUrl)} coverColor={canAdminister ? f.coverColor : group.coverColor}
        iconColor={canAdminister ? f.iconColor : group.iconColor}
        sub={group.admin ? <>Led by <b>@{group.admin.username}</b></> : 'No leader'} />

      {/* Registration panel — same behaviour as Events (design/024): shown to
          everyone except the leader, including existing members (the server
          dedupes and returns alreadyMember=true). Preview shows it too. */}
      {(!isGroupAdmin || preview) && (
        <GroupRegisterPanel group={group} fromHome={fromHome} purchase={purchase}
          isMember={!preview && !!(me?.id && (group.members || []).some((m) => m.id === me.id))} />
      )}

      <div className="grid-2">
        <div>
          <Section title={canAdminister ? 'Group details' : 'About'}>
            {canAdminister ? (
              <>
                <div className="form-field"><label>Cover color</label>
                  <ColorField value={f.coverColor} onChange={(hex) => setF((p) => ({ ...p, coverColor: hex }))} />
                </div>
                <IconColorField value={f.iconColor} coverValue={f.coverColor}
                  onChange={(v) => setF((p) => ({ ...p, iconColor: v }))} />
                <div className="form-field">
                  <label>Icon image (optional — shown instead of the icon color)</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Avatar name={f.name} colorSeed={id} src={api.iconUrl(iconUrl)} square size={48}
                      bg={(f.iconColor || f.coverColor) ? cssBackground(f.iconColor || f.coverColor) : undefined} />
                    <input ref={iconRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onIcon} />
                    <button type="button" className="btn btn-sm" disabled={iconBusy} onClick={() => iconRef.current?.click()}>
                      {iconBusy ? 'Uploading…' : (iconUrl ? 'Replace image' : 'Upload image')}
                    </button>
                    {iconUrl && (
                      <button type="button" className="btn btn-sm btn-danger" disabled={iconBusy} onClick={removeIcon}>Remove</button>
                    )}
                  </div>
                </div>
                <div className="form-field"><label>Name <span className="req">*</span></label><input className="input" value={f.name} onChange={set('name')} /></div>
                <div className="form-field"><label>Description</label><textarea className="textarea" value={f.description} onChange={set('description')} /></div>
                <div className="form-field"><label>Location</label><input className="input" value={f.location} onChange={set('location')} placeholder="address, zoom link, etc." /></div>
                <PriceField cents={f.priceCents}
                  onChangeCents={(v) => setF((p) => ({ ...p, priceCents: v }))}
                  paidHint="Paid — non-members pay via Stripe on the web to join."
                  freeHint="$0 = free (anyone can join)." />
                <AnnounceToggle kind="group" id={id} isPrivate={!!f.isPrivate} />
                <div className="form-field">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={!!f.isPrivate}
                      onChange={(e) => setF((p) => ({ ...p, isPrivate: e.target.checked }))} />
                    <span>Private group</span>
                  </label>
                  <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>Hidden from the public catalog. Only the leader and invited members can see it.</div>
                </div>
              </>
            ) : (
              <>
                <div className="item-body" style={{ marginBottom: group.location ? 8 : 0 }}>
                  {group.description || <span style={{ color: 'var(--muted-2)' }}>(no description)</span>}
                </div>
                {group.location && !preview && <div className="item-leader">📍 {group.location}</div>}
                {((!group.location && group.detailsHidden) || (preview && group.location)) && (
                  <div className="item-leader">📍 🔒 Location shared after you register</div>
                )}
                {/* 2026-10-16 — members see "You're a member" instead of the price. */}
                {!preview && me?.id && (group.members || []).some((m) => m.id === me.id) ? (
                  <div style={{ marginTop: 8 }}>
                    <span className="pill" style={{ background: 'var(--green-bg, #dcfce7)', color: 'var(--green-text, #166534)', fontWeight: 700 }}>✓ You're a member</span>
                  </div>
                ) : group.priceCents > 0 && (
                  <div className="item-leader" style={{ marginTop: group.location ? 4 : 8 }}>
                    🎟 Ticket ${(group.priceCents / 100).toFixed(2)}
                  </div>
                )}
                {group.isPrivate && <div className="item-leader" style={{ marginTop: 4 }}>🔒 Private (invite-only)</div>}
              </>
            )}
          </Section>

          {canAdminister && <AssociationTabs sourceKind="group" sourceId={id} />}

          {canAdminister && !justCreated && (
            <NotificationComposer scopeType="GROUP" scopeId={id} scopeName={group.name} />
          )}
        </div>

        <div>
          {/* Hide Danger zone on freshly-created drafts (?created=1 in URL).
              Cleared by the first successful Save. */}
          {canAdminister && !justCreated && (
            <Section>
              <div className="danger-zone">
                <div className="label">Danger zone</div>
                <button className="btn btn-danger" style={{ width: '100%', justifyContent: 'center' }} onClick={del}>
                  Delete group
                </button>
              </div>
            </Section>
          )}
        </div>
      </div>

      {/* design/026 — members-only conversation (teaser for everyone else). */}
      {!preview && canAdminister && justCreated && (
        <ConversationSetup value={convSetup} onChange={setConvSetup} />
      )}
      {!preview && !(canAdminister && justCreated) && (
        <Conversation kind="group" id={id}
          key={`conv-${!!(me?.id && (group.members || []).some((m) => m.id === me.id))}`}
          onRegister={() => navigate(`/group/${id}/register`, { state: fromHome ? { from: 'home' } : undefined })} />
      )}

      <RelatedStrips sourceKind="group" sourceId={id} />

      {previewing && (
        <PreviewModal kind="group" onClose={() => setPreviewing(false)}>
          <GroupDetailPage preview />
        </PreviewModal>
      )}
    </div>
  );
}

/**
 * Registration panel for Groups (parity with Events' RegisterPanel). Single
 * "Register →" CTA that routes to the shared wizard for free and paid groups,
 * plus the return banners:
 *   ?registered=1     → freshly joined a free group (or already a member).
 *   ?checkout=success → Stripe redirected back after paying.
 *   ?checkout=cancel  → Stripe checkout cancelled.
 */
function GroupRegisterPanel({ group, fromHome, isMember = false, purchase }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = params.get('checkout');
  const registered = params.get('registered') === '1';
  const paid = (group.priceCents || 0) > 0;
  const price = paid ? `$${(group.priceCents / 100).toFixed(2)}` : 'Free';

  const dismiss = () => {
    const p = new URLSearchParams(params);
    p.delete('checkout'); p.delete('session_id'); p.delete('registered');
    setParams(p, { replace: true });
  };

  const go = () => navigate(`/group/${group.id}/register`, { state: fromHome ? { from: 'home' } : undefined });

  return (
    <>
      {((status === 'success' && (purchase?.confirming || purchase?.failed || isMember)) || (registered && isMember)) && (
        <div className="section" style={{ background: 'var(--green-bg, #dcfce7)', border: '1px solid #86efac', borderRadius: 10, padding: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ flex: 1, color: '#166534' }}>
            {status === 'success' && purchase?.confirming ? <strong>Confirming your payment…</strong>
              : status === 'success' && purchase?.failed && !isMember
                ? <><strong>Payment received.</strong> Your membership is still being confirmed — refresh this page in a minute.</>
                : <><strong>You're registered — welcome to the group.</strong>{paid ? ' Payment received.' : ''}</>}
          </div>
          <button className="btn" onClick={dismiss}>Dismiss</button>
        </div>
      )}
      {status === 'cancel' && (
        <div className="section" style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ flex: 1, color: '#991b1b' }}>Checkout canceled — you haven't been charged.</div>
          <button className="btn" onClick={dismiss}>Dismiss</button>
        </div>
      )}
      {/* Member → nothing here (decided 2026-10-02: no permanent badge). */}
      {isMember ? null : (
      <Section>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              {paid ? `${price} — one-time` : 'Free group'}
            </div>
            <div style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>
              {paid
                ? 'Register to become a member and get group updates.'
                : 'Register to join and get group updates.'}
            </div>
          </div>
          <button className="btn btn-primary" onClick={go}>Register →</button>
        </div>
      </Section>
      )}
    </>
  );
}

/**
 * @deprecated Superseded by GroupRegisterPanel. Kept for reference.
 * Buy-to-Join panel + Stripe redirect banner for a paid Group (V37). Renders
 * only when the group has a fee and the viewer isn't the leader.
 */
/**
 * "Join group" CTA for free Groups. Calls the self-join endpoint and
 * reloads on success so the caller shows up in the members list right
 * away and the panel hides itself.
 */
function FreeJoinPanel({ group, onJoined }) {
  const [joining, setJoining] = useState(false);
  const join = async () => {
    setJoining(true);
    try {
      await api.joinGroup(group.id);
      onJoined?.();
    } catch (e) { alert(e.message); setJoining(false); }
  };
  return (
    <Section>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Free group</div>
          <div style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Join to see chat + participate in group events.</div>
        </div>
        <button className="btn btn-primary" disabled={joining} onClick={join}>
          {joining ? 'Joining…' : 'Join group'}
        </button>
      </div>
    </Section>
  );
}

function JoinPanel({ group }) {
  const [params, setParams] = useSearchParams();
  const [buying, setBuying] = useState(false);
  const status = params.get('checkout');

  const buy = async () => {
    setBuying(true);
    try {
      const { url } = await api.groupCheckout(group.id);
      if (url) window.location.assign(url);
    } catch (e) { alert(e.message); setBuying(false); }
  };
  const dismiss = () => {
    const p = new URLSearchParams(params); p.delete('checkout'); p.delete('session_id');
    setParams(p, { replace: true });
  };
  const price = `$${(group.priceCents / 100).toFixed(2)}`;

  return (
    <>
      {status === 'success' && (
        <div className="section" style={{ background: 'var(--green-bg, #dcfce7)', border: '1px solid #86efac', borderRadius: 10, padding: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ flex: 1, color: '#166534' }}><strong>You're in.</strong> Payment received — you're now a member.</div>
          <button className="btn" onClick={dismiss}>Dismiss</button>
        </div>
      )}
      {status === 'cancel' && (
        <div className="section" style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: 14, display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ flex: 1, color: '#991b1b' }}>Checkout canceled — you haven't been charged.</div>
          <button className="btn" onClick={dismiss}>Dismiss</button>
        </div>
      )}
      <Section>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{price} — one-time</div>
            <div style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>Buy a ticket to become a member of this group.</div>
          </div>
          <button className="btn btn-primary" disabled={buying} onClick={buy}>
            {buying ? 'Redirecting…' : `Buy Ticket — ${price}`}
          </button>
        </div>
      </Section>
    </>
  );
}
